import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Sparkles, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  formatDuration,
  formatLead,
  JOB_TYPES_KEY,
  jobTypesApi,
  type ImportedJobType,
  type ImportPreview,
} from './job-types-api';

const ACCEPT = '.xlsx,.csv,.pdf,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * ייבוא סוגי עבודה מקובץ: Excel / CSV (לפי כותרות העמודות) או PDF (קריאה
 * אוטומטית). שום דבר לא נשמר עד שהבעלים בוחר ולוחץ "ייבוא".
 */
export function ImportJobTypesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['job-types', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const read = useMutation({
    mutationFn: (file: File) => jobTypesApi.previewImport(file),
    onSuccess: (p) => {
      setPreview(p);
      setSelected(new Set(p.items.map((_, i) => i)));
    },
    onError: (e) => toast.error(t('common:errors.readFileFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const save = useMutation({
    mutationFn: (items: ImportedJobType[]) => jobTypesApi.importMany(items),
    onSuccess: async ({ created, skipped }) => {
      toast.success(
        created ? t('import.imported', { count: created }) : t('import.nothing'),
        skipped.length
          ? t('import.skipped', { names: `${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? '…' : ''}` })
          : undefined,
      );
      await qc.invalidateQueries({ queryKey: JOB_TYPES_KEY });
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.importFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const pick = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('common:errors.fileTooLarge', { mb: 5 }));
      return;
    }
    setFileName(file.name);
    setPreview(null);
    read.mutate(file);
  };

  const toggle = (i: number) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const items = preview?.items ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="import-jt-desc" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription id="import-jt-desc">
            {t('import.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            aria-label={t('import.chooseFileAria')}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => input.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pick(e.dataTransfer.files[0]);
            }}
            className="flex w-full flex-col items-center gap-2 rounded-(--radius-lg) border border-dashed border-border-strong bg-surface-sunken px-4 py-6 text-center transition-colors hover:border-accent hover:bg-accent-subtle focus-visible:outline-2 focus-visible:outline-accent"
          >
            {read.isPending ? (
              <span className="text-sm text-fg-muted">{t('import.reading', { name: fileName })}</span>
            ) : (
              <>
                <Upload className="size-5 text-fg-subtle" aria-hidden />
                <span className="text-sm font-medium text-fg">{fileName ?? t('import.dropzone')}</span>
                <span className="text-xs text-fg-muted">{t('import.formats')}</span>
              </>
            )}
          </button>

          {preview ? (
            <section aria-live="polite" className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-fg">
                  {t('import.found', { count: items.length })}
                </p>
                <Badge tone={preview.source === 'ai' ? 'info' : 'neutral'}>
                  {preview.source === 'ai' ? (
                    <>
                      <Sparkles className="size-3" aria-hidden /> {t('import.sourceAi')}
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="size-3" aria-hidden /> {t('import.sourceTable')}
                    </>
                  )}
                </Badge>
              </div>
              {preview.warnings.map((w) => (
                <p key={w} className="rounded-(--radius-md) bg-warning-subtle px-3 py-2 text-xs text-warning">
                  {w}
                </p>
              ))}
              {items.length ? (
                <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-(--radius-md) border border-border">
                  {items.map((item, i) => (
                    <li key={`${item.name}-${i}`}>
                      <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-surface-sunken">
                        <Checkbox checked={selected.has(i)} onCheckedChange={() => toggle(i)} aria-label={item.name} className="mt-0.5" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-fg">{item.name}</span>
                          <span className="block text-xs text-fg-muted">
                            {[
                              item.requiredSkill,
                              item.durationMinutes != null ? formatDuration(item.durationMinutes) : null,
                              item.leadTimeDays != null ? formatLead(item.leadTimeDays) : null,
                              item.checklist.length ? item.checklist.join(' · ') : null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || t('import.noDetails')}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={selected.size === 0 || !preview}
            loading={save.isPending}
            onClick={() => save.mutate(items.filter((_, i) => selected.has(i)))}
          >
            {selected.size ? t('import.submit', { count: selected.size }) : t('import.submitNone')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
